import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  TrendingUp, 
  AlertTriangle, 
  Gamepad2, 
  Layers, 
  GitCompare, 
  Search, 
  Target, 
  Trophy,
  Flame,
  Calendar,
  ChevronDown,
  ChevronRight,
  BookOpen,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Sparkles,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GAME_INFO = {
  flashcards: { title: 'Flashcards', icon: Layers, color: 'text-blue-600', bg: 'bg-blue-100' },
  matching: { title: 'Matching', icon: GitCompare, color: 'text-purple-600', bg: 'bg-purple-100' },
  wordsearch: { title: 'Word Search', icon: Search, color: 'text-emerald-600', bg: 'bg-emerald-100' }
};

export default function Progress() {
  const navigate = useNavigate();
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTopics, setExpandedTopics] = useState({});
  const [topicSort, setTopicSort] = useState('accuracy'); // accuracy, attempts, recent

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/progress/summary`, { withCredentials: true });
      setProgressData(response.data);
      
      // Debug log (dev only)
      if (process.env.NODE_ENV !== 'production') {
        console.log('[Progress] API Response:', response.data);
      }
    } catch (err) {
      console.error('[Progress] API Error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load progress data');
      toast.error('Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 60) return `${seconds || 0}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const toggleTopic = (topicKey) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicKey]: !prev[topicKey]
    }));
  };

  const getSortedTopics = () => {
    if (!progressData?.topic_breakdown) return [];
    const topics = [...progressData.topic_breakdown];
    
    switch (topicSort) {
      case 'accuracy':
        return topics.sort((a, b) => a.accuracy_percent - b.accuracy_percent);
      case 'attempts':
        return topics.sort((a, b) => b.attempted_count - a.attempted_count);
      case 'recent':
        return topics.sort((a, b) => {
          const dateA = a.last_activity_date || '';
          const dateB = b.last_activity_date || '';
          return dateB.localeCompare(dateA);
        });
      default:
        return topics;
    }
  };

  const getRecommendations = () => {
    if (!progressData) return [];
    const recommendations = [];
    
    // Weak topics (accuracy < 60%)
    const weakTopics = (progressData.topic_breakdown || []).filter(t => t.accuracy_percent < 60 && t.attempted_count >= 3);
    weakTopics.slice(0, 2).forEach(topic => {
      recommendations.push({
        type: 'review',
        icon: AlertTriangle,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        title: `Review: ${topic.topic_key}`,
        description: `${topic.accuracy_percent}% accuracy - needs improvement`,
        action: () => navigate(`/quiz/setup/${encodeURIComponent(topic.topic_key)}`),
        buttonText: 'Start Quiz'
      });
    });
    
    // No game activity
    if (progressData.reinforcement_activities?.sessions_played < 3) {
      recommendations.push({
        type: 'games',
        icon: Gamepad2,
        color: 'text-purple-600',
        bg: 'bg-purple-50',
        title: 'Reinforce with Games',
        description: 'Play matching or flashcards to strengthen memory',
        action: () => navigate('/games'),
        buttonText: 'Play Games'
      });
    }
    
    // No case studies
    if (progressData.case_studies?.cases_completed === 0) {
      recommendations.push({
        type: 'cases',
        icon: FileText,
        color: 'text-indigo-600',
        bg: 'bg-indigo-50',
        title: 'Try Case Studies',
        description: 'Apply knowledge to clinical scenarios',
        action: () => navigate('/case-studies'),
        buttonText: 'Start Cases'
      });
    }
    
    // Low overall quiz activity
    if (progressData.quiz_performance?.total_questions_attempted < 10) {
      recommendations.push({
        type: 'quiz',
        icon: Target,
        color: 'text-teal-600',
        bg: 'bg-teal-50',
        title: 'Build Your Foundation',
        description: 'Take more quizzes to track your progress',
        action: () => navigate('/quiz/setup'),
        buttonText: 'Start Quiz'
      });
    }
    
    return recommendations.slice(0, 3);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-500">Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md bg-white">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Failed to Load Progress</h3>
            <p className="text-slate-500 mb-4">{error}</p>
            <Button onClick={fetchProgress} className="bg-teal-600 hover:bg-teal-700 text-white">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const quiz = progressData?.quiz_performance || {};
  const games = progressData?.reinforcement_activities || {};
  const cases = progressData?.case_studies || {};
  const streak = progressData?.streak || {};
  const topics = getSortedTopics();
  const recentActivity = progressData?.recent_activity || [];
  const recommendations = getRecommendations();

  const hasAnyActivity = quiz.total_questions_attempted > 0 || games.sessions_played > 0 || cases.cases_completed > 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Button
            data-testid="progress-back-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-teal-100 rounded-lg">
                <TrendingUp className="w-8 h-8 text-teal-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Your Progress</h1>
                <p className="text-slate-500">Track your learning journey</p>
              </div>
            </div>
            {streak.current_streak_days > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-full border border-orange-200">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="font-semibold text-orange-700">{streak.current_streak_days} day streak!</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Hero Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <Target className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{quiz.total_questions_attempted || 0}</div>
                  <div className="text-xs text-slate-500">Questions Attempted</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className={`text-2xl font-bold ${
                    quiz.overall_accuracy_percent >= 80 ? 'text-green-600' :
                    quiz.overall_accuracy_percent >= 60 ? 'text-amber-600' : 'text-red-500'
                  }`}>
                    {quiz.overall_accuracy_percent || 0}%
                  </div>
                  <div className="text-xs text-slate-500">Overall Accuracy</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Gamepad2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{games.sessions_played || 0}</div>
                  <div className="text-xs text-slate-500">Games Played</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <FileText className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{cases.cases_completed || 0}</div>
                  <div className="text-xs text-slate-500">Cases Completed</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* No Activity State */}
        {!hasAnyActivity && (
          <Card className="bg-white border-slate-200 mb-8">
            <CardContent className="py-12 text-center">
              <Sparkles className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Start Your Learning Journey</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                Complete quizzes, play games, or work through case studies to track your progress here.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button onClick={() => navigate('/quiz/setup')} className="bg-teal-600 hover:bg-teal-700 text-white">
                  <Target className="w-4 h-4 mr-2" />
                  Start a Quiz
                </Button>
                <Button onClick={() => navigate('/games')} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50">
                  <Gamepad2 className="w-4 h-4 mr-2" />
                  Play Games
                </Button>
                <Button onClick={() => navigate('/case-studies')} variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                  <FileText className="w-4 h-4 mr-2" />
                  Case Studies
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {hasAnyActivity && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Main Stats */}
            <div className="lg:col-span-2 space-y-6">
              {/* Quiz Performance Section */}
              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Target className="w-5 h-5 text-teal-600" />
                    Quiz Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <div className="text-2xl font-bold text-slate-900">{quiz.total_questions_attempted || 0}</div>
                      <div className="text-xs text-slate-500">Total Questions</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{quiz.correct_answers || 0}</div>
                      <div className="text-xs text-slate-500">Correct</div>
                    </div>
                    <div className="text-center p-3 bg-teal-50 rounded-lg">
                      <div className={`text-2xl font-bold ${
                        quiz.overall_accuracy_percent >= 80 ? 'text-green-600' :
                        quiz.overall_accuracy_percent >= 60 ? 'text-amber-600' : 'text-red-500'
                      }`}>
                        {quiz.overall_accuracy_percent || 0}%
                      </div>
                      <div className="text-xs text-slate-500">Accuracy</div>
                    </div>
                  </div>
                  {quiz.last_quiz_date && (
                    <div className="text-xs text-slate-400 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Last quiz: {formatDate(quiz.last_quiz_date)}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Games/Reinforcement Section */}
              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Gamepad2 className="w-5 h-5 text-purple-600" />
                    Reinforcement Activities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {games.sessions_played > 0 ? (
                    <>
                      <div className="grid grid-cols-4 gap-3 mb-4">
                        <div className="text-center p-2 bg-slate-50 rounded-lg">
                          <div className="text-xl font-bold text-slate-900">{games.sessions_played}</div>
                          <div className="text-xs text-slate-500">Sessions</div>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded-lg">
                          <div className="text-xl font-bold text-slate-900">{games.items_completed}</div>
                          <div className="text-xs text-slate-500">Items</div>
                        </div>
                        <div className="text-center p-2 bg-purple-50 rounded-lg">
                          <div className="text-xl font-bold text-purple-600">{games.game_accuracy_percent}%</div>
                          <div className="text-xs text-slate-500">Accuracy</div>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded-lg">
                          <div className="text-xl font-bold text-slate-900">{formatTime(games.total_time_seconds)}</div>
                          <div className="text-xs text-slate-500">Time</div>
                        </div>
                      </div>
                      
                      {/* Game Type Breakdown */}
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(GAME_INFO).map(([gameType, info]) => {
                          const stats = games.breakdown?.[gameType];
                          const Icon = info.icon;
                          
                          return (
                            <div key={gameType} className={`p-3 rounded-lg border ${stats ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`p-1.5 ${info.bg} rounded`}>
                                  <Icon className={`w-3 h-3 ${info.color}`} />
                                </div>
                                <span className="text-sm font-medium text-slate-700">{info.title}</span>
                              </div>
                              {stats ? (
                                <div className="space-y-1 text-xs">
                                  <div className="flex justify-between text-slate-500">
                                    <span>Sessions</span>
                                    <span className="font-medium text-slate-700">{stats.sessions}</span>
                                  </div>
                                  <div className="flex justify-between text-slate-500">
                                    <span>Accuracy</span>
                                    <span className={`font-medium ${info.color}`}>{stats.accuracy}%</span>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-xs text-slate-400">Not played</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <Gamepad2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm mb-3">No games played yet</p>
                      <Button onClick={() => navigate('/games')} size="sm" variant="outline" className="text-purple-600 border-purple-300">
                        Explore Games
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Case Studies Section */}
              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    Case Studies
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {cases.cases_completed > 0 ? (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-indigo-50 rounded-lg">
                        <div className="text-2xl font-bold text-indigo-600">{cases.cases_completed}</div>
                        <div className="text-xs text-slate-500">Completed</div>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <div className="text-2xl font-bold text-slate-900">{cases.avg_score_percent}%</div>
                        <div className="text-xs text-slate-500">Avg Score</div>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <div className="text-sm font-medium text-slate-700">{formatDate(cases.last_case_date)}</div>
                        <div className="text-xs text-slate-500">Last Case</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm mb-3">No cases completed yet</p>
                      <Button onClick={() => navigate('/case-studies')} size="sm" variant="outline" className="text-indigo-600 border-indigo-300">
                        Start a Case
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Topic Breakdown Section */}
              {topics.length > 0 && (
                <Card className="bg-white border-slate-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <BookOpen className="w-5 h-5 text-slate-600" />
                        By Topic
                      </CardTitle>
                      <div className="flex gap-1">
                        {[
                          { key: 'accuracy', label: 'Weak First' },
                          { key: 'attempts', label: 'Most Practiced' },
                          { key: 'recent', label: 'Recent' }
                        ].map(sort => (
                          <Button
                            key={sort.key}
                            variant={topicSort === sort.key ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setTopicSort(sort.key)}
                            className={topicSort === sort.key ? 'bg-slate-900 text-white text-xs' : 'text-xs text-slate-500'}
                          >
                            {sort.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {topics.map(topic => (
                      <div key={topic.topic_key} className="border border-slate-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleTopic(topic.topic_key)}
                          className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {expandedTopics[topic.topic_key] ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                            <span className="font-medium text-slate-900">{topic.topic_display || topic.topic_key}</span>
                            <Badge variant="outline" className="text-xs bg-slate-50">
                              {topic.attempted_count} attempts
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all ${
                                  topic.accuracy_percent >= 80 ? 'bg-green-500' :
                                  topic.accuracy_percent >= 60 ? 'bg-amber-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${topic.accuracy_percent}%` }}
                              />
                            </div>
                            <span className={`text-sm font-semibold w-12 text-right ${
                              topic.accuracy_percent >= 80 ? 'text-green-600' :
                              topic.accuracy_percent >= 60 ? 'text-amber-600' : 'text-red-500'
                            }`}>
                              {topic.accuracy_percent}%
                            </span>
                          </div>
                        </button>
                        
                        {expandedTopics[topic.topic_key] && topic.subtopics?.length > 0 && (
                          <div className="bg-slate-50 px-3 pb-3 pt-1 space-y-1">
                            {topic.subtopics.map(sub => (
                              <div key={sub.subtopic_key} className="flex items-center justify-between py-1.5 px-2 bg-white rounded border border-slate-100">
                                <span className="text-sm text-slate-600">{sub.subtopic_display || sub.subtopic_key}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-400">{sub.attempted_count} attempts</span>
                                  <span className={`text-sm font-medium ${
                                    sub.accuracy_percent >= 80 ? 'text-green-600' :
                                    sub.accuracy_percent >= 60 ? 'text-amber-600' : 'text-red-500'
                                  }`}>
                                    {sub.accuracy_percent}%
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Activity & Recommendations */}
            <div className="space-y-6">
              {/* Streak & Last Active */}
              <Card className="bg-white border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${streak.current_streak_days > 0 ? 'bg-orange-100' : 'bg-slate-100'}`}>
                        <Flame className={`w-5 h-5 ${streak.current_streak_days > 0 ? 'text-orange-500' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-slate-900">
                          {streak.current_streak_days > 0 ? `${streak.current_streak_days} Day Streak` : 'No Active Streak'}
                        </div>
                        <div className="text-xs text-slate-500">
                          {streak.total_active_days || 0} total active days
                        </div>
                      </div>
                    </div>
                  </div>
                  {streak.last_active_date && (
                    <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Last active: {formatDate(streak.last_active_date)}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recommendations */}
              {recommendations.length > 0 && (
                <Card className="bg-white border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {recommendations.map((rec, idx) => {
                      const Icon = rec.icon;
                      return (
                        <div key={idx} className={`p-3 rounded-lg ${rec.bg} border border-slate-100`}>
                          <div className="flex items-start gap-2">
                            <Icon className={`w-4 h-4 ${rec.color} mt-0.5`} />
                            <div className="flex-1">
                              <div className="font-medium text-slate-900 text-sm">{rec.title}</div>
                              <div className="text-xs text-slate-500 mt-0.5">{rec.description}</div>
                              <Button
                                onClick={rec.action}
                                size="sm"
                                variant="ghost"
                                className={`mt-2 h-7 px-2 text-xs ${rec.color}`}
                              >
                                {rec.buttonText}
                                <ArrowRight className="w-3 h-3 ml-1" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* Recent Activity */}
              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Clock className="w-4 h-4 text-slate-500" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentActivity.length > 0 ? (
                    <div className="space-y-2">
                      {recentActivity.slice(0, 8).map((activity, idx) => (
                        <div key={idx} className="flex items-start gap-2 py-2 border-b border-slate-50 last:border-0">
                          <div className={`p-1 rounded ${
                            activity.type === 'quiz' ? 'bg-teal-100' :
                            activity.type === 'game' ? 'bg-purple-100' : 'bg-indigo-100'
                          }`}>
                            {activity.type === 'quiz' ? (
                              activity.is_correct ? 
                                <CheckCircle2 className="w-3 h-3 text-green-600" /> : 
                                <XCircle className="w-3 h-3 text-red-500" />
                            ) : activity.type === 'game' ? (
                              <Gamepad2 className="w-3 h-3 text-purple-600" />
                            ) : (
                              <FileText className="w-3 h-3 text-indigo-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-900 truncate">{activity.summary_text}</div>
                            <div className="text-xs text-slate-400 flex items-center gap-2">
                              <span>{activity.topic}</span>
                              {activity.score_or_accuracy !== undefined && activity.type !== 'quiz' && (
                                <Badge variant="outline" className="text-xs px-1 py-0">
                                  {activity.score_or_accuracy}%
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-slate-400 whitespace-nowrap">
                            {formatDate(activity.created_at)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">No recent activity</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
